import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import {
  getWindowFormDetail,
  getWindowFormMessages,
  postWindowFormMessage,
  postWindowFormQuote,
  updateWindowFormStatus,
} from "../../../components/Forms/api";
import { normalizeDate, sendUserNotification } from "../../../utilities";
import {
  ChatComposer,
  MessageCard,
  StatusBadge,
  WorkflowStepper,
  personName,
} from "./WindowFormShared";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import {
  BackLink,
  Segmented,
  inputCls,
} from "../../../components/WindowDoor/wdUi";

// Human wording for the status notifications sent to the salesperson.
const STATUS_NOTIFICATION_MESSAGES = {
  "Under Review": "Your window RFQ is now under review.",
  "Needs Revision": "The window team needs changes before they can quote your RFQ.",
  "Reviewing Revisions": "The window team is reviewing your revisions.",
  "Awaiting Sales Approval": "A quote is ready for your review.",
  Approved: "Your window RFQ quote was marked approved.",
  "Converted to Order": "Your window RFQ has been converted to an order.",
  Cancelled: "Your window RFQ was cancelled.",
};

const ALL_STATUSES = [
  "Submitted",
  "Under Review",
  "Awaiting Sales Approval",
  "Needs Revision",
  "Reviewing Revisions",
  "Approved",
  "Converted to Order",
  "Cancelled",
];

// Quoter / window-admin view of a single RFQ. The main job here is: read the
// RFQ, work the quote, and move it along the pipeline with one click.
const WindowFormDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const user = useSelector(selectUser);
  const [formData, setFormData] = useState(null);
  const [internalMessages, setInternalMessages] = useState([]);
  const [sharedMessages, setSharedMessages] = useState([]);
  const [internalText, setInternalText] = useState("");
  const [sharedText, setSharedText] = useState("");
  const [activeChatTab, setActiveChatTab] = useState("internal");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which action panel is expanded: quote | changes | order | cancel | manual
  const [panel, setPanel] = useState(null);
  const [quoteFile, setQuoteFile] = useState(null);
  const [quoteNote, setQuoteNote] = useState("");
  const [quoteError, setQuoteError] = useState("");
  const [changeNotes, setChangeNotes] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [manualStatus, setManualStatus] = useState("Submitted");
  const internalChatRef = useRef(null);
  const sharedChatRef = useRef(null);

  useEffect(() => {
    getWindowFormDetail(setFormData, id);
    getWindowFormMessages(setInternalMessages, id, "internal");
    getWindowFormMessages(setSharedMessages, id, "shared");
  }, [id]);

  useEffect(() => {
    if (formData) {
      setOrderNumber(formData.order_number || "");
      setManualStatus(formData.status || "Submitted");
    }
  }, [formData]);

  useEffect(() => {
    const scrollContainer =
      activeChatTab === "internal" ? internalChatRef : sharedChatRef;
    if (scrollContainer?.current) {
      scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight;
    }
  }, [activeChatTab, internalMessages, sharedMessages]);

  const status = formData?.status || "Submitted";
  const canManageStatus =
    formData &&
    (formData?.assigned_to?.userID === user?.userID ||
      user?.roles?.includes("Window Door Manager") ||
      user?.roles?.includes("Admin"));

  const backPath = routeLocation.pathname.includes("/windowdoor/staff")
    ? "/windowdoor/staff/windowforms"
    : "/windowdoor/quote-manager";
  const backLabel = routeLocation.pathname.includes("/windowdoor/staff")
    ? "My Assigned RFQs"
    : "Quote Manager";

  const refresh = async () => {
    await getWindowFormDetail(setFormData, id);
    await getWindowFormMessages(setSharedMessages, id, "shared");
  };

  const closePanels = () => {
    setPanel(null);
    setQuoteError("");
  };

  // In-app notification to the salesperson who submitted the RFQ. Older
  // entries store submit_user as a plain string with no email — skip those,
  // and don't notify yourself.
  const notifySales = (subject, message) => {
    const salesEmail = formData?.submit_user?.email;
    if (!salesEmail || salesEmail === user?.email) {
      return;
    }
    sendUserNotification(
      salesEmail,
      user?.name || "Window Door Team",
      message,
      subject,
      `/windowdoor/sales/windowforms/${formData._id}`
    );
  };

  const applyStatus = async (nextStatus, overrides = {}) => {
    if (!formData?._id) {
      return;
    }
    setBusy(true);
    await updateWindowFormStatus(formData._id, {
      status: nextStatus,
      status_notes: formData?.status_notes || "",
      order_number: formData?.order_number || "",
      cancelled_reason: formData?.cancelled_reason || "",
      ...overrides,
      updated_by: {
        name: user?.name,
        userID: user?.userID,
        email: user?.email,
      },
    });
    const detail = overrides.status_notes || overrides.cancelled_reason || "";
    notifySales(
      `Window RFQ: ${nextStatus}`,
      `${
        STATUS_NOTIFICATION_MESSAGES[nextStatus] ||
        `Your window RFQ status changed to ${nextStatus}.`
      }${detail ? ` "${detail}"` : ""}`
    );
    await refresh();
    setBusy(false);
    closePanels();
  };

  const handleSendQuote = async () => {
    if (!quoteFile) {
      setQuoteError("Attach the quote PDF before sending.");
      return;
    }
    setQuoteError("");
    setBusy(true);
    if (status !== "Awaiting Sales Approval") {
      await updateWindowFormStatus(formData._id, {
        status: "Awaiting Sales Approval",
        status_notes: formData?.status_notes || "",
        order_number: formData?.order_number || "",
        cancelled_reason: formData?.cancelled_reason || "",
        updated_by: {
          name: user?.name,
          userID: user?.userID,
          email: user?.email,
        },
      });
    }
    const payload = new FormData();
    payload.append("file", quoteFile);
    payload.append("userName", user?.name || "");
    payload.append("userID", user?.userID || "");
    payload.append("userEmail", user?.email || "");
    if (quoteNote.trim()) {
      payload.append("note", quoteNote.trim());
    }
    await postWindowFormQuote(id, payload, "shared");
    notifySales(
      "Window quote ready for review",
      `${user?.name || "The window team"} sent a quote for your review.`
    );
    setQuoteFile(null);
    setQuoteNote("");
    await refresh();
    setBusy(false);
    closePanels();
  };

  const handleSend = async (channel) => {
    const trimmed =
      channel === "internal" ? internalText.trim() : sharedText.trim();
    if (!trimmed) {
      return;
    }
    setSending(true);
    await postWindowFormMessage(
      id,
      {
        message: trimmed,
        user: {
          name: user?.name,
          userID: user?.userID,
          email: user?.email,
        },
      },
      channel
    );
    if (channel === "internal") {
      setInternalText("");
      await getWindowFormMessages(setInternalMessages, id, "internal");
    } else {
      setSharedText("");
      await getWindowFormMessages(setSharedMessages, id, "shared");
    }
    setSending(false);
  };

  const togglePanel = (key) => {
    setQuoteError("");
    setPanel((prev) => (prev === key ? null : key));
  };

  const primaryButton = "app-btn app-btn-primary";
  const secondaryButton = "app-btn app-btn-secondary";
  const inputClass = inputCls;

  const statusHints = {
    Submitted: "New RFQ — start the review to let everyone know it's being worked.",
    "Under Review":
      "Work the quote, then send it to sales — or ask sales for changes if something is missing.",
    "Needs Revision": "Waiting on sales to update the RFQ.",
    "Reviewing Revisions":
      "Sales updated the RFQ — check the revision in chat, then send an updated quote.",
    "Awaiting Sales Approval":
      "Quote sent — sales will approve it or come back with changes.",
    Approved:
      "Sales approved the quote. Once it's entered in Bistrack, record the order number.",
    "Converted to Order": "This RFQ is done — the order has been placed.",
    Cancelled: "This RFQ was cancelled.",
  };

  const renderQuotePanel = () => (
    <div className="mt-4 space-y-3 rounded-xl border border-indigo-500/25 bg-indigo-500/10 p-4">
      <p className="text-sm font-semibold">
        {status === "Awaiting Sales Approval"
          ? "Send an updated quote to sales"
          : "Send the quote to sales for approval"}
      </p>
      <input
        type="file"
        accept=".pdf"
        onChange={(event) => setQuoteFile(event.target.files?.[0] || null)}
        aria-label="Quote PDF"
        className="block w-full text-sm file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-indigo-600 file:px-4 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700 disabled:opacity-50"
        disabled={busy}
      />
      <textarea
        className={inputClass}
        rows={2}
        placeholder="Optional note for the salesperson..."
        value={quoteNote}
        onChange={(event) => setQuoteNote(event.target.value)}
        disabled={busy}
      />
      {quoteError && (
        <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-700 dark:text-rose-300">
          {quoteError}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className={secondaryButton} onClick={closePanels} disabled={busy}>
          Never mind
        </button>
        <button type="button" className={primaryButton} onClick={handleSendQuote} disabled={busy}>
          {busy ? "Sending..." : "Send Quote"}
        </button>
      </div>
    </div>
  );

  const renderChangesPanel = () => (
    <div className="mt-4 space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
      <p className="text-sm font-semibold">Ask sales for changes</p>
      <textarea
        className={inputClass}
        rows={3}
        placeholder="What needs to change or be clarified?"
        value={changeNotes}
        onChange={(event) => setChangeNotes(event.target.value)}
        disabled={busy}
      />
      <div className="flex justify-end gap-2">
        <button type="button" className={secondaryButton} onClick={closePanels} disabled={busy}>
          Never mind
        </button>
        <button
          type="button"
          className={primaryButton}
          onClick={() =>
            applyStatus("Needs Revision", { status_notes: changeNotes.trim() })
          }
          disabled={busy || !changeNotes.trim()}
        >
          {busy ? "Sending..." : "Request Changes"}
        </button>
      </div>
    </div>
  );

  const renderOrderPanel = () => (
    <div className="mt-4 space-y-3 rounded-xl border border-teal-500/25 bg-teal-500/10 p-4">
      <p className="text-sm font-semibold">Record the Bistrack order number</p>
      <input
        className={inputClass}
        placeholder="Order number"
        value={orderNumber}
        onChange={(event) => setOrderNumber(event.target.value)}
        disabled={busy}
      />
      <div className="flex justify-end gap-2">
        <button type="button" className={secondaryButton} onClick={closePanels} disabled={busy}>
          Never mind
        </button>
        <button
          type="button"
          className={primaryButton}
          onClick={() =>
            applyStatus("Converted to Order", {
              order_number: orderNumber.trim(),
            })
          }
          disabled={busy || !orderNumber.trim()}
        >
          {busy ? "Saving..." : "Mark as Ordered"}
        </button>
      </div>
    </div>
  );

  const renderCancelPanel = () => (
    <div className="mt-4 space-y-3 rounded-xl border border-rose-500/25 bg-rose-500/10 p-4">
      <p className="text-sm font-semibold">Cancel this RFQ</p>
      <textarea
        className={inputClass}
        rows={2}
        placeholder="Reason for cancelling..."
        value={cancelReason}
        onChange={(event) => setCancelReason(event.target.value)}
        disabled={busy}
      />
      <div className="flex justify-end gap-2">
        <button type="button" className={secondaryButton} onClick={closePanels} disabled={busy}>
          Never mind
        </button>
        <button
          type="button"
          className="app-btn app-btn-danger"
          onClick={() =>
            applyStatus("Cancelled", { cancelled_reason: cancelReason.trim() })
          }
          disabled={busy || !cancelReason.trim()}
        >
          {busy ? "Saving..." : "Cancel RFQ"}
        </button>
      </div>
    </div>
  );

  const renderManualPanel = () => (
    <div className="mt-4 space-y-3 rounded-xl border border-slate-400/30 bg-slate-400/10 p-4">
      <p className="text-sm font-semibold">Set status manually</p>
      <p className="app-muted text-xs">
        For corrections only — the buttons above handle the normal flow.
      </p>
      <select
        className={inputClass}
        value={manualStatus}
        onChange={(event) => setManualStatus(event.target.value)}
        disabled={busy}
      >
        {ALL_STATUSES.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <div className="flex justify-end gap-2">
        <button type="button" className={secondaryButton} onClick={closePanels} disabled={busy}>
          Never mind
        </button>
        <button
          type="button"
          className={primaryButton}
          onClick={() => applyStatus(manualStatus)}
          disabled={busy || manualStatus === status}
        >
          {busy ? "Saving..." : "Save Status"}
        </button>
      </div>
    </div>
  );

  const renderActions = () => {
    if (!canManageStatus) {
      return (
        <p className="app-muted text-sm">
          Status updates are available to the assigned quoter or a manager.
        </p>
      );
    }
    const actions = [];
    if (status === "Submitted") {
      actions.push(
        <button
          key="start"
          type="button"
          className={primaryButton}
          onClick={() => applyStatus("Under Review")}
          disabled={busy}
        >
          {busy ? "Saving..." : "Start Review"}
        </button>
      );
    }
    if (
      ["Under Review", "Needs Revision", "Reviewing Revisions"].includes(status)
    ) {
      actions.push(
        <button
          key="quote"
          type="button"
          className={primaryButton}
          onClick={() => togglePanel("quote")}
          disabled={busy}
        >
          Send Quote to Sales
        </button>
      );
      if (status !== "Needs Revision") {
        actions.push(
          <button
            key="changes"
            type="button"
            className={secondaryButton}
            onClick={() => togglePanel("changes")}
            disabled={busy}
          >
            Ask Sales for Changes
          </button>
        );
      }
    }
    if (status === "Awaiting Sales Approval") {
      actions.push(
        <button
          key="requote"
          type="button"
          className={secondaryButton}
          onClick={() => togglePanel("quote")}
          disabled={busy}
        >
          Send Updated Quote
        </button>
      );
    }
    if (status === "Approved") {
      actions.push(
        <button
          key="order"
          type="button"
          className={primaryButton}
          onClick={() => togglePanel("order")}
          disabled={busy}
        >
          Mark Converted to Order
        </button>
      );
    }
    return (
      <>
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
        {panel === "quote" && renderQuotePanel()}
        {panel === "changes" && renderChangesPanel()}
        {panel === "order" && renderOrderPanel()}
        {panel === "cancel" && renderCancelPanel()}
        {panel === "manual" && renderManualPanel()}
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t app-divider pt-3 text-xs">
          {!["Cancelled", "Converted to Order"].includes(status) && (
            <button
              type="button"
              className="min-h-[32px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
              onClick={() => togglePanel("cancel")}
              disabled={busy}
            >
              Cancel RFQ
            </button>
          )}
          <button
            type="button"
            className="app-muted min-h-[32px] font-semibold hover:text-[color:var(--app-text)]"
            onClick={() => togglePanel("manual")}
            disabled={busy}
          >
            Set status manually
          </button>
        </div>
      </>
    );
  };

  const detailRows = formData
    ? [
        ["Customer", formData.customer],
        ["Account", formData.account],
        ["Job", formData.job],
        ["Form", formData.form_name || "Window Form"],
        ["Form Type", formData.form_type?.toUpperCase()],
        ["Submitted By", personName(formData.submit_user)],
        ["Assigned To", formData.assigned_to?.name || "Unassigned"],
        ["Order Number", formData.order_number],
      ].filter(([, value]) => value)
    : [];

  const chatTabs = [
    {
      key: "internal",
      label: "Team Only",
      count: internalMessages.length,
    },
    {
      key: "shared",
      label: "With Sales",
      count: sharedMessages.length,
    },
  ];

  const activeMessages =
    activeChatTab === "internal" ? internalMessages : sharedMessages;

  return (
    <PageShell size="2xl">
      <BackLink onClick={() => navigate(backPath)}>
        Back to {backLabel}
      </BackLink>

      {!formData ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading RFQ…" />
        </div>
      ) : (
        <>
          <PageHeader
            icon={RequestQuoteOutlinedIcon}
            tone="indigo"
            eyebrow="Window Quotes & Forms"
            title={formData.customer || "Window RFQ"}
            description={
              <>
                {[
                  formData.form_name || "Window Form",
                  formData.account,
                  formData.job,
                ]
                  .filter(Boolean)
                  .join(" • ")}
                <span className="mt-0.5 block text-xs">
                  Submitted by {personName(formData.submit_user)}
                  {formData.entry_date &&
                    ` on ${normalizeDate(new Date(formData.entry_date))}`}
                </span>
              </>
            }
            actions={<StatusBadge status={status} />}
          />

          <div className="app-card mb-6 rounded-2xl border p-5">
            <WorkflowStepper
              status={status}
              audience="team"
              detail={
                status === "Needs Revision"
                  ? formData.status_notes
                  : status === "Cancelled"
                  ? formData.cancelled_reason
                  : ""
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <section className="app-card rounded-2xl border p-5">
                <h2 className="text-lg font-semibold">Next Step</h2>
                <p className="app-muted mb-4 mt-0.5 text-sm">
                  {statusHints[status]}
                </p>
                {renderActions()}
              </section>

              <section className="app-card rounded-2xl border p-5">
                <h2 className="mb-4 text-lg font-semibold">RFQ Details</h2>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
                  {detailRows.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="app-muted text-xs font-medium">{label}</dt>
                      <dd className="mt-0.5 break-words font-semibold">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="app-card rounded-2xl border p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">RFQ Document</h2>
                  <a
                    className="app-btn app-btn-secondary app-btn-sm"
                    href={`/api/forms/pdf/?id=${formData._id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                    Open in new tab
                  </a>
                </div>
                <iframe
                  title="Window RFQ PDF"
                  src={`/api/forms/pdf/?id=${formData._id}`}
                  className="h-[560px] w-full rounded-xl border app-divider bg-white"
                />
              </section>
            </div>

            <aside className="app-card h-fit rounded-2xl border p-4">
              <Segmented
                fill
                ariaLabel="Chat channel"
                className="mb-3"
                value={activeChatTab}
                onChange={setActiveChatTab}
                options={chatTabs.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                  badge:
                    tab.count > 0 ? (
                      <span className="app-muted text-xs font-medium tabular-nums">
                        {tab.count}
                      </span>
                    ) : null,
                }))}
              />
              <p className="app-muted mb-2 px-1 text-xs">
                {activeChatTab === "internal"
                  ? "Only the window team can see these messages."
                  : "The salesperson can see everything here."}
              </p>
              <div
                ref={activeChatTab === "internal" ? internalChatRef : sharedChatRef}
                className="max-h-[420px] space-y-3 overflow-y-auto rounded-xl border app-divider p-3"
              >
                {activeMessages.length === 0 && (
                  <EmptyState
                    compact
                    icon={ChatBubbleOutlineIcon}
                    description="No messages yet. Start the conversation."
                  />
                )}
                {activeMessages.map((message, index) => (
                  <MessageCard
                    key={`${message.createdAt}-${index}`}
                    message={message}
                    isOwn={message?.user?.userID === user?.userID}
                  />
                ))}
              </div>
              <ChatComposer
                value={activeChatTab === "internal" ? internalText : sharedText}
                onChange={
                  activeChatTab === "internal" ? setInternalText : setSharedText
                }
                onSend={() => handleSend(activeChatTab)}
                disabled={sending}
                placeholder={
                  activeChatTab === "internal"
                    ? "Message the window team..."
                    : "Message the salesperson..."
                }
                buttonLabel={sending ? "Sending..." : "Send"}
                buttonClass={
                  activeChatTab === "internal"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-sky-600 hover:bg-sky-700"
                }
              />
            </aside>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default WindowFormDetail;
